import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/config';
import useStore from '../store/useStore';

const TEAMS = ['CR','LOS','LMS','Leasing','HR','Sales','Sysadmin','Accounting'];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { setUser, setToken, darkMode, toggleDarkMode } = useStore();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({
    email:'', otp:'', full_name:'', password:'', confirm_password:'', department:''
  });

  const sendOTP = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error('Enter your email');
    if (!/@qualtechedge\.(com|in)$/.test(form.email)) return toast.error('Only @qualtechedge.com or @qualtechedge.in emails allowed');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/send-otp`, { email: form.email });
      toast.success('OTP sent to your email!');
      setStep(2);
    } catch(err) {
      const msg = err.response?.data?.message || 'Failed to send OTP';
      if (msg.includes('already registered') || msg.includes('already exist')) {
        toast.error('This email is already registered!', {duration:3000});
        setTimeout(() => navigate('/login'), 2000);
      } else {
        toast.error(msg);
      }
    }
    finally { setLoading(false); }
  };

  const register = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.password || !form.otp) return toast.error('Fill all fields');
    // department is optional
    if (form.password !== form.confirm_password) return toast.error('Passwords do not match');
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/register`, {
        email: form.email, otp: form.otp, full_name: form.full_name,
        password: form.password, department: form.department, team: form.department
      });
      setToken(data.token); setUser(data.user);
      toast.success(`Welcome to Qtalk, ${data.user.full_name}!`);
      navigate('/profile-setup');
    } catch(err) {
      const msg = err.response?.data?.message || 'Registration failed';
      if (msg.includes('already registered') || msg.includes('already exist')) {
        toast.error('This email is already registered. Please login instead.', {duration: 4000});
        setTimeout(() => navigate('/login'), 2000);
      } else {
        toast.error(msg);
      }
    }
    finally { setLoading(false); }
  };

  return (
    <div className={`fixed inset-0 flex ${darkMode?'dark':''}`}>
      {/* Left panel */}
      <div className="hidden lg:flex w-5/12 bg-gradient-to-b from-teal-500 to-blue-600 items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl"/>
        </div>
        <img src="/logo.png" alt="Qtalk" className="relative z-10 w-64 object-contain"
          style={{filter:'brightness(0) invert(1)', mixBlendMode:'screen'}}/>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col bg-white dark:bg-gray-950 overflow-auto">
        <div className="flex items-center justify-between px-8 py-5">
          <a href="/login" className="flex items-center gap-2 text-gray-400 hover:text-teal-500 transition text-sm">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Back to login
          </a>
          <button onClick={toggleDarkMode} className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition text-sm">
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-8 py-4">
          <div className="w-full max-w-sm">
            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-6">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step>=1?'bg-gradient-to-br from-teal-500 to-blue-500 text-white':'bg-gray-200 dark:bg-gray-700 text-gray-400'}`}>1</div>
              <div className={`flex-1 h-0.5 rounded transition-all ${step>=2?'bg-gradient-to-r from-teal-500 to-blue-500':'bg-gray-200 dark:bg-gray-700'}`}/>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step>=2?'bg-gradient-to-br from-teal-500 to-blue-500 text-white':'bg-gray-200 dark:bg-gray-700 text-gray-400'}`}>2</div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {step===1 ? 'Create account' : 'Complete profile'}
            </h2>
            <p className="text-gray-400 text-sm mb-6">
              {step===1 ? 'Use your @qualtechedge.com email' : `OTP sent to ${form.email}`}
            </p>

            {step===1 ? (
              <form onSubmit={sendOTP} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Work Email</label>
                  <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}
                    placeholder="you@qualtechedge.com" autoFocus
                    className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-400 transition text-sm"/>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white font-semibold rounded-xl transition shadow-sm disabled:opacity-50 text-sm">
                  {loading ? 'Sending OTP...' : 'Send OTP →'}
                </button>
                <p className="text-center text-sm text-gray-400">
                  Already have an account?{' '}
                  <Link to="/login" className="text-teal-500 font-semibold hover:text-teal-600">Sign in</Link>
                </p>
              </form>
            ) : (
              <form onSubmit={register} className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">OTP Code</label>
                  <input type="text" value={form.otp} onChange={e=>setForm({...form,otp:e.target.value})}
                    placeholder="6-digit code" maxLength={6}
                    className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-400 transition text-sm font-mono tracking-widest" autoFocus/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Full Name</label>
                  <input type="text" value={form.full_name} onChange={e=>setForm({...form,full_name:e.target.value})}
                    placeholder="Your full name"
                    className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-400 transition text-sm"/>
                </div>

                {/* Department - optional free text */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                    Department <span className="text-gray-300 dark:text-gray-600 font-normal normal-case">(optional)</span>
                  </label>
                  <input type="text" value={form.department} onChange={e=>setForm({...form,department:e.target.value})}
                    placeholder="e.g. Sysadmin, HR, Sales, LOS..."
                    className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-400 transition text-sm"/>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Password</label>
                  <div className="relative mt-1.5">
                    <input type={showPass?'text':'password'} value={form.password} onChange={e=>setForm({...form,password:e.target.value})}
                      placeholder="Min 8 characters"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-400 transition text-sm pr-11"/>
                    <button type="button" onClick={()=>setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition p-1">
                      {showPass
                        ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Confirm Password</label>
                  <input type="password" value={form.confirm_password} onChange={e=>setForm({...form,confirm_password:e.target.value})}
                    placeholder="Repeat password"
                    className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-400 transition text-sm"/>
                </div>

                <button type="submit" disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white font-semibold rounded-xl transition shadow-sm disabled:opacity-50 text-sm mt-2">
                  {loading ? 'Creating account...' : 'Create Account →'}
                </button>

                <button type="button" onClick={()=>setStep(1)} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition">
                  ← Change email
                </button>
              </form>
            )}
          </div>
        </div>
        <div className="px-8 py-4 text-center">
          <p className="text-xs text-gray-200 dark:text-gray-700">© 2026 QualTechEdge · Internal Use Only</p>
        </div>
      </div>
    </div>
  );
}
